import { useCallback, useMemo, useState } from 'react';
import {
  SearchFilterType,
  type QuickFilter,
  type SaveSearchFilterPayload,
} from '../types';
import { useNavigate } from 'react-router-dom';
import {
  currentSearchFilterInputNeeded,
  isNumericString,
  joinFilterTokens,
  convertToFilterString,
  sanitizeSearchFilterTokens,
} from '../utils';
import { useDispatch, useSelector } from 'react-redux';
import type { AppDispatch, RootState } from '../../../../store';
import {
  setCurrentFilter,
  setFromNavBar,
  setUserSavedFilters,
} from '../../../../store/ai2';
import { post } from '../../../../api/baseUrl';
import { SelectionStatus } from './useFilterSelection';
import { SystemPermission } from '../../../../context';

const usePatientSearch = (
  activeFilters: QuickFilter[],
  currentFlow: SearchFilterType | null,
  flowStatus: SelectionStatus,
) => {
  const [isSaving, setIsSaving] = useState(false);
  const { selectedTenancy } = useSelector(
    (state: RootState) => state.clinicianProfile,
  );
  const { userSavedFilters } = useSelector(
    (state: RootState) => state.dashboardSearch,
  );
  const savedSearchDisabled =
    selectedTenancy?.features?.searchScreen?.savedSearchDisabled ?? false;
  const dispatch: AppDispatch = useDispatch();
  const navigate = useNavigate();

  const [searchText, setSearchText] = useState('');
  const [onFocusOnSearch, setOnFocusOnSearch] = useState(true);
  const [showSavedSearch, setShowSavedSearch] = useState(false);
  const searchFiltersMap = selectedTenancy?.features?.searchFiltersMap || {};
  const canAccessOpenSafetyAlerts =
    !selectedTenancy?.permissionsSetup ||
    !!selectedTenancy?.userPermissions?.[SystemPermission.SAFETY_MONITORING];
  const canAccessReadyForReview =
    !selectedTenancy?.permissionsSetup ||
    !selectedTenancy?.userPermissions?.[SystemPermission.DEIDENTIFIED_DATA];

  const quickFiltersToStringArray = (filters: QuickFilter[]): string[] => {
    return filters.flatMap(({ label, value }) =>
      value ? [label, value] : [label],
    );
  };

  const searchPatients = (searchFilters?: QuickFilter[]) => {
    const filters = searchFilters || activeFilters;
    const filterStrings = quickFiltersToStringArray(filters);
    const joinedFilters = joinFilterTokens(filterStrings, searchFiltersMap);
    const sanitizedFilters = sanitizeSearchFilterTokens(
      joinedFilters,
      searchFiltersMap,
      {
        allowOpenSafetyAlerts: canAccessOpenSafetyAlerts,
        allowReadyForReview: canAccessReadyForReview,
      },
    );
    dispatch(setFromNavBar(false));
    dispatch(setCurrentFilter(sanitizedFilters));
    setShowSavedSearch(false);
    return navigate('/dashboard');
  };

  const onSaveSearchToPatient = async (filters: QuickFilter[]) => {
    if (savedSearchDisabled) return;
    if (!selectedTenancy) return;
    const filterText = quickFiltersToStringArray(filters).join(', ');

    try {
      setIsSaving(true);
      const payload: SaveSearchFilterPayload = {
        filterText,
        tenancyId: selectedTenancy.tenancy_id,
      };
      const {
        data: { data },
      } = await post('/saveSearchFilter', payload);
      dispatch(setUserSavedFilters([...userSavedFilters, data]));
      return data;
    } catch (error) {
      console.error(error);
      return null;
    } finally {
      setIsSaving(false);
    }
  };

  const onSelectSavedFilter = useCallback(
    async (id: string) => {
      if (savedSearchDisabled) return;
      // 1. Find the saved filter by id
      const savedFilter = userSavedFilters.find((filter) => filter.id === id);
      if (!savedFilter) return;

      const filters = convertToFilterString(savedFilter, searchFiltersMap);
      const sanitizedFilters = sanitizeSearchFilterTokens(
        filters,
        searchFiltersMap,
        {
          allowOpenSafetyAlerts: canAccessOpenSafetyAlerts,
          allowReadyForReview: canAccessReadyForReview,
        },
      );
      dispatch(setFromNavBar(false));
      dispatch(setCurrentFilter(sanitizedFilters));
      setShowSavedSearch(false);
      return navigate('/dashboard');
    },
    [
      dispatch,
      navigate,
      canAccessOpenSafetyAlerts,
      canAccessReadyForReview,
      savedSearchDisabled,
      searchFiltersMap,
      userSavedFilters,
    ],
  );

  const showSaveSearchButton = useMemo(
    () => !savedSearchDisabled,
    [savedSearchDisabled],
  );

  const onChangeSearchText = useCallback((text: string) => {
    if (flowStatus === SelectionStatus.AGE && !isNumericString(text)) {
      return;
    }
    if (flowStatus === SelectionStatus.SPECIFIC_PATIENT) {
      return;
    }
    if (currentFlow && currentSearchFilterInputNeeded(currentFlow)) {
      return;
    }
    setSearchText(text);
  }, []);

  const toggleSavedSearch = () => {
    setShowSavedSearch((showSavedSearch) => !showSavedSearch);
    setOnFocusOnSearch(false);
  };

  const onFocusedSearch = () => {
    setOnFocusOnSearch(true);
    setShowSavedSearch(false);
  };

  const onUnfocusedSearch = () => {
    setOnFocusOnSearch(false);
  };

  return {
    onUnfocusedSearch,
    onFocusedSearch,
    showSaveSearchButton,
    onFocusOnSearch,
    showSavedSearch,
    searchText,
    searchPatients,
    onChangeSearchText,
    toggleSavedSearch,
    savedSearchFilters: userSavedFilters,
    onSelectSavedFilter,
    setSearchText,
    onSaveSearchToPatient,
    isSaving,
    quickFiltersToStringArray,
  };
};

export { usePatientSearch };
